import { useMemo } from 'react';
import { scadStringToHex, OPENSCAD_NAMED_COLORS } from '@/utils/colorUtils';
import type { Parameter } from '@shared/types';

type RGBA = [number, number, number, number];

/**
 * Maps 1-based line numbers to RGBA colour tuples for every `color(...)` call
 * found in the SCAD source that matches one of the compiled output colours.
 *
 * Handles both inline color literals (`color([1,0,0])`, `color("red")`) and
 * variable references (`color(body_color)`) by resolving variables against:
 *   1. The current parameter values (live, for color-type parameters)
 *   2. The variable assignment in the source code (static fallback)
 *
 * Only the line containing the `color(` keyword is marked.
 */
export function useColorLineMap(
  scadCode: string,
  compiledColors: RGBA[],
  parameters?: Parameter[],
): Map<number, RGBA> {
  return useMemo(() => {
    if (!scadCode || compiledColors.length === 0) return new Map();

    // Build a variable→RGBA lookup from parameters (takes precedence — live values).
    const paramColorMap = new Map<string, RGBA>();
    if (parameters) {
      for (const p of parameters) {
        const rgba = paramValueToRgba(p.value);
        if (rgba) paramColorMap.set(p.name, rgba);
      }
    }

    // Build a variable→RGBA lookup from source code assignments.
    const sourceVarMap = buildSourceVarMap(scadCode);

    const lines = scadCode.split('\n');
    const result = new Map<number, RGBA>();

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];

      // Quick pre-filter — skip lines that clearly have no color() call.
      if (!line.includes('color(')) continue;

      // Extract the argument string from color(...).
      const colorCallMatch = line.match(/\bcolor\s*\(([^)]*)\)/);
      if (!colorCallMatch) continue;

      const argStr = colorCallMatch[1].trim();

      // Try to resolve the argument as an RGBA value.
      let rgba = parseColorArg(argStr);

      // If that failed, try treating it as a variable name.
      if (!rgba) {
        const varName = argStr.match(/^(\$?[a-zA-Z_][a-zA-Z0-9_]*)/)?.[1];
        if (varName) {
          rgba =
            paramColorMap.get(varName) ??
            sourceVarMap.get(varName) ??
            null;
        }
      }

      if (!rgba) continue;

      // Find the closest match in compiled colours (tolerance for float math).
      const matched = findClosestColor(rgba, compiledColors);
      if (matched) {
        result.set(i + 1, matched); // 1-based line number
      }
    }

    return result;
  }, [scadCode, compiledColors, parameters]);
}

// ---------------------------------------------------------------------------
// Variable resolution
// ---------------------------------------------------------------------------

/**
 * Scan the source for simple variable assignments like:
 *   body_color = [0.486, 0.349, 0.192];
 *   my_color = "red";
 * Returns a map of variable name → RGBA.
 */
function buildSourceVarMap(code: string): Map<string, RGBA> {
  const map = new Map<string, RGBA>();
  const lines = code.split('\n');
  // Match:  varName = value ;  (value is captured up to the first `;`)
  const assignRe = /^\s*(\$?[a-zA-Z_][a-zA-Z0-9_]*)\s*=\s*([^;]+);/;

  for (const line of lines) {
    const m = line.match(assignRe);
    if (!m) continue;
    const name = m[1];
    const valueStr = m[2].trim();
    const rgba = parseColorArg(valueStr);
    if (rgba) map.set(name, rgba);
  }

  return map;
}

/**
 * Convert a Parameter value (array or string) to an RGBA tuple, if it looks
 * like a colour value. Returns null for non-colour parameters.
 */
function paramValueToRgba(value: Parameter['value']): RGBA | null {
  if (Array.isArray(value)) {
    const nums = (value as unknown[]).map(Number).filter((n) => !isNaN(n));
    if (nums.length < 3) return null;
    const is255 = nums.some((n) => n > 1);
    const [r, g, b] = nums.slice(0, 3).map((v) => clamp01(is255 ? v / 255 : v));
    const a = nums.length >= 4 ? clamp01(is255 ? nums[3] / 255 : nums[3]) : 1;
    return [r, g, b, a];
  }
  if (typeof value === 'string') {
    return parseColorArg(`"${value}"`);
  }
  return null;
}

// ---------------------------------------------------------------------------
// Color argument parser
// ---------------------------------------------------------------------------

/**
 * Parse an OpenSCAD color() argument string into an RGBA tuple [0-1 each].
 *
 * Supported forms:
 *   "red"                    → named color
 *   "#FF0000"                → hex string
 *   [1, 0, 0]                → RGB array
 *   [1, 0, 0, 0.5]           → RGBA array
 *   [1, 0, 0], 0.5           → RGB array + alpha as second argument
 *   "red", 0.5               → named color + alpha as second argument
 */
function parseColorArg(argStr: string): RGBA | null {
  const trimmed = argStr.trim();
  if (!trimmed) return null;

  // ── String color: "red" or "#FF0000" ──────────────────────────────────────
  const stringMatch = trimmed.match(/^"([^"]+)"/);
  if (stringMatch) {
    const colorStr = stringMatch[1];
    const hex = scadStringToHex(colorStr);
    if (!hex) return null;
    const rgb = hexToRgb01(hex);
    if (!rgb) return null;
    // Check for second alpha argument: "red", 0.5
    const alphaMatch = trimmed.match(/^"[^"]+"\s*,\s*([\d.]+)/);
    const alpha = alphaMatch ? clamp01(parseFloat(alphaMatch[1])) : 1;
    return [rgb[0], rgb[1], rgb[2], alpha];
  }

  // ── Array color: [r, g, b] or [r, g, b, a] ───────────────────────────────
  const arrayMatch = trimmed.match(/^\[([^\]]+)\]/);
  if (arrayMatch) {
    const parts = arrayMatch[1].split(',').map((s) => parseFloat(s.trim()));
    if (parts.length < 3 || parts.some(isNaN)) return null;

    // Detect 0–255 range
    const is255 = parts.some((v) => v > 1);
    const [r, g, b] = parts.slice(0, 3).map((v) =>
      clamp01(is255 ? v / 255 : v),
    );

    let alpha = 1;
    if (parts.length >= 4) {
      alpha = clamp01(is255 ? parts[3] / 255 : parts[3]);
    } else {
      // Check for second alpha argument: [r,g,b], 0.5
      const alphaMatch = trimmed.match(/^\[[^\]]+\]\s*,\s*([\d.]+)/);
      if (alphaMatch) alpha = clamp01(parseFloat(alphaMatch[1]));
    }

    return [r, g, b, alpha];
  }

  // ── Bare named color without quotes (uncommon but valid in some contexts) ──
  const namedMatch = trimmed.match(/^([a-zA-Z]+)\s*$/);
  if (namedMatch) {
    const key = namedMatch[1].toLowerCase();
    if (key in OPENSCAD_NAMED_COLORS) {
      const hex = `#${OPENSCAD_NAMED_COLORS[key]}`;
      const rgb = hexToRgb01(hex);
      if (rgb) return [rgb[0], rgb[1], rgb[2], 1];
    }
  }

  return null;
}

// ---------------------------------------------------------------------------
// Utilities
// ---------------------------------------------------------------------------

/** Convert a #RRGGBB hex string to [r, g, b] in 0-1 range. */
function hexToRgb01(hex: string): [number, number, number] | null {
  const clean = hex.replace('#', '');
  if (clean.length < 6) return null;
  const r = parseInt(clean.slice(0, 2), 16) / 255;
  const g = parseInt(clean.slice(2, 4), 16) / 255;
  const b = parseInt(clean.slice(4, 6), 16) / 255;
  if (isNaN(r) || isNaN(g) || isNaN(b)) return null;
  return [r, g, b];
}

function clamp01(v: number): number {
  return Math.max(0, Math.min(1, v));
}

const COLOR_TOLERANCE = 0.05; // ~13/255 — enough tolerance for float rounding + parameter precision

/**
 * Find the compiled colour closest to `target` within tolerance.
 * Returns the compiled colour (not the parsed source value) so the bar uses
 * the exact same RGBA the viewer rendered.
 */
function findClosestColor(
  target: RGBA,
  candidates: RGBA[],
): RGBA | null {
  let bestDist = Infinity;
  let best: RGBA | null = null;

  for (const c of candidates) {
    const dr = c[0] - target[0];
    const dg = c[1] - target[1];
    const db = c[2] - target[2];
    // Ignore alpha when matching — OpenSCAD often passes alpha separately
    const dist = Math.sqrt(dr * dr + dg * dg + db * db);
    if (dist < bestDist) {
      bestDist = dist;
      best = c;
    }
  }

  return bestDist <= COLOR_TOLERANCE ? best : null;
}
